<template>
	<BuyersOrderGroup
		:name="bucketType"
		:isOpenByDefault="validation.vehicle.bookValues.$invalid"
	>
		<template #group-action>
			<span
				v-if="addNewItemPrompt"
				class="new-tax-line"
			>
				<button
					class="button button-edit"
					@click="onAddBookValueClick()"
				>
					<i class="fas fa-plus"></i> {{ addNewItemPrompt }}
				</button>
			</span>
		</template>

		<template #right-icon>
			<div
				v-if="BookValuesAreInvalid"
				class="error-text bo-bucket-error"
			>
				<i class="fas fa-exclamation-circle"></i>
			</div>
		</template>

		<template #children>
			<BuyersOrderRow
				v-for="(bookValue, index) in vehicle.bookValues"
				:key="bookValue.source"
			>
				<template #info>
					<button
						type="button"
						@click="onEditBookValueClick(bookValue)"
						:class="{ 'bucket-item-name-container': true, 'button-link': true }"
					>
						<div class="bucket-item-name">
							{{ BookValue[bookValue.source] }}
							<!-- <div  class="bucket-item-subname"></div> -->
						</div>
					</button>
				</template>

				<template #column-1>
					<!-- <InputRichDropdown
                        label="Valuation Company"
                        :saturn.sync="item.source"
                        :list="bookValuesDropDown()"
                        :value="valuePredicate"
                        :display="displayPredicate"
                        search
                    /> -->
				</template>

				<template #column-2>
					<InputCurrency
						:label="bookValue.source == BookValue.Manufacturer ? 'MSRP' : 'Retail'"
						v-model:saturn="bookValue.retail"
						:precision="2"
						iconSide="left"
						@change="(val: number) => (bookValue.retail = val)"
						:invalid="validation.vehicle.bookValues[index]?.retail.$invalid"
						:negative="false"
						:disabled="true"
					>
						<template #validation>
							<span v-if="InvalidAll(bookValue, 'retail')">At least one book value with retail and wholesale is required</span>
							<span v-else-if="!validation.vehicle.bookValues[index]?.retail">Retail price is required</span>
							<span v-else-if="validation.vehicle.bookValues[index]?.retail.valid == false">Retail price must be higher than $0</span>
						</template>
					</InputCurrency>
				</template>

				<template #column-3>
					<InputCurrency
						:label="bookValue.source == BookValue.Manufacturer ? 'Invoice' : 'Wholesale'"
						v-model:saturn="bookValue.wholesale"
						:precision="2"
						iconSide="left"
						:invalid="validation.vehicle.bookValues[index]?.wholesale.$invalid"
						@change="(val: number) => (bookValue.wholesale = val)"
						:negative="false"
						:disabled="true"
					>
						<template #validation>
							<span v-if="InvalidAll(bookValue, 'wholesale')">At least one book value with retail and wholesale is required</span>
							<span v-else-if="!validation.vehicle.bookValues[index]?.wholesale">Wholesale price is required</span>
							<span v-else-if="validation.vehicle.bookValues[index]?.wholesale.valid == false">Wholesale price must be higher than $0</span>
						</template>
					</InputCurrency>
				</template>
			</BuyersOrderRow>
		</template>
	</BuyersOrderGroup>
</template>

<script lang="ts">
	export interface IBuyersOrderBookValuesProps {
		bucketType: string;
		addNewItemPrompt: string;
		vehicle: FIMenuVehicle;
		isVehicleNew: boolean;
		validation: any;
	}
</script>

<script setup lang="ts">
	import { BookValue } from '@core/classes/SharedEnums';
	import BuyersOrderGroup from './BuyersOrderGroup.vue';
	import BuyersOrderRow from './BuyersOrderRow.vue';
	import { computed } from 'vue';
	import FIMenuVehicle from '@core/classes/FIMenuVehicle';
	import { IBookValueContent } from '@core/_interfaces/SharedInterfaces';
	import InputCurrency from '@core/components/InputCurrency.vue';
	import { VehicleBookValue } from '@core/classes/VehicleBookValue';

	const { bucketType, addNewItemPrompt, vehicle, isVehicleNew, validation } = defineProps<IBuyersOrderBookValuesProps>();

	const emit = defineEmits<{
		addBookValueClick: [bookValueContent: IBookValueContent];
		editBookValueClick: [bookValueContent: IBookValueContent];
	}>();

	const BookValuesAreInvalid = computed(() => {
		return validation.vehicle.bookValues.$invalid;
	});
	const BookValuesWithoutManufacturer = computed(() => {
		return vehicle.bookValues?.filter(x => x.source != BookValue.Manufacturer) ?? [];
	});
	const AtleastOneBookValueWithRetailAndWholeSaleOnNewDeal = computed(() => {
		return !isVehicleNew && !BookValuesWithoutManufacturer.value.some(x => !!x.retail && !!x.wholesale);
	});

	// If vehicle is new. We must always create Manufacturer to include MSRP; as it is a required field.
	if (isVehicleNew) {
		vehicle.getOrCreateManufacturer();
	}

	function onAddBookValueClick() {
		emit('addBookValueClick', { row: bucketType });
	}
	function onEditBookValueClick(bookValue: VehicleBookValue) {
		emit('editBookValueClick', { row: bucketType, bookValue });
	}
	function InvalidAll(bookValue: VehicleBookValue, which: 'wholesale' | 'retail') {
		return (
			bookValue.source != BookValue.Manufacturer &&
			!bookValue[which] &&
			(!bookValue.wholesale || !bookValue.retail) &&
			AtleastOneBookValueWithRetailAndWholeSaleOnNewDeal.value
		);
	}

	// function displayPredicate(val) {
	//     return val.display;
	// }
	// function valuePredicate(val) {
	//     return val.value;
	// }
	// function bookValuesDropDown() {
	//     return GetBookValueDropdownValues();
	// }
	// function sourceMustMatchManufacturer(values) {
	//     return values.some(value => value.source === BookValue.Manufacturer);
	// }
</script>

<style>
	.bucket-item-name {
		width: 100%;
	}

	.bucket-item-name-container.clickable {
		color: var(--main-color);
	}

	.bucket-item-name-container {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100% !important;
	}

	.bucket-item-subname {
		position: relative;
		font-size: 13px;
		color: var(--black-60percent);
		overflow: hidden;
		width: 100%;
	}

	.bucket-item-subname .content {
		position: absolute;
		text-wrap: nowrap;
	}

	.bo-bucket-sign {
		position: relative;
	}

	.bo-bucket-error {
		position: absolute;
		right: 20px;
	}
</style>
